import { app, checkpoint } from '../checkpointHelper/app';
import { isSecondaryCheckpointUser } from '../checkpointHelper/static/auth-types';
import type { SecondaryUser } from '../checkpoint/secondary-users';
import { SETTINGS_APP_NAME } from './config';
import { fetchSettingsWorkspace, saveSettingsTeams } from './pm-api';
import type { Team, User } from './types';
import { isSettingsAdmin } from './authz';

export const SUPER_ADMIN_ROLE = 'super_admin';

export let users: User[] = [];
export let teams: Team[] = [];
export let editingTeamId: number | null = null;

let currentUser: User | null = null;
let persistenceEnabled = false;
let workspaceSaveTimer: ReturnType<typeof setTimeout> | null = null;

function replaceArrayInPlace<T>(target: T[], next: T[]): void {
    target.length = 0;
    target.push(...next);
}

function currentAuthUserId(): number {
    const authUser = app.window.user as { id?: number | string } | null;
    const raw = authUser?.id ?? app.window.member?.user_id ?? 0;
    return Number(raw);
}

function secondaryUserToAppUser(su: SecondaryUser): User {
    const grant = su.apps?.[SETTINGS_APP_NAME];
    return {
        id: su.id,
        name: su.username || su.email,
        email: su.email,
        role: grant?.role || 'developer',
        permissions: grant?.permissions ?? [],
        checkpointUserId: su.id,
    };
}

function ownerToAppUser(): User {
    const authUser = app.window.user as { id?: number | string; email?: string; username?: string } | null;
    const member = app.window.member as { user_id?: number | string; email?: string; username?: string } | null;
    const email = (authUser?.email ?? member?.email ?? '').trim();
    return {
        id: currentAuthUserId(),
        name: authUser?.username || member?.username || email || 'Owner',
        email,
        role: SUPER_ADMIN_ROLE,
        permissions: [],
        checkpointUserId: null,
    };
}

export async function loadUsersFromCheckpoint(): Promise<void> {
    const next: User[] = [];

    if (app.window.userType === 'normal') {
        next.push(ownerToAppUser());
    }

    try {
        const result = await checkpoint.secondaryUsers.list({
            appName: SETTINGS_APP_NAME,
            limit: 200,
        });
        for (const su of result.users) {
            next.push(secondaryUserToAppUser(su));
        }
    } catch (error) {
        console.warn('Failed to load secondary users from Checkpoint:', error);
    }

    const authId = currentAuthUserId();
    if (!next.some((u) => u.id === authId)) {
        const authUser = app.window.user;
        next.push(
            app.window.userType === 'secondary' && authUser && isSecondaryCheckpointUser(authUser)
                ? secondaryUserToAppUser(authUser)
                : ownerToAppUser(),
        );
    }

    const seen = new Map<number, User>();
    for (const u of next) {
        if (!seen.has(u.id)) seen.set(u.id, u);
    }
    replaceArrayInPlace(users, [...seen.values()]);
}

async function resolveCurrentUserFromAuth(): Promise<void> {
    const authId = currentAuthUserId();
    let appUser =
        users.find((u) => u.id === authId) ??
        users.find((u) => u.checkpointUserId === authId) ??
        null;

    if (!appUser) {
        const authUser = app.window.user;
        appUser =
            app.window.userType === 'secondary' && authUser && isSecondaryCheckpointUser(authUser)
                ? secondaryUserToAppUser(authUser)
                : ownerToAppUser();
        users.push(appUser);
    }

    currentUser = appUser;
}

export function getCurrentUser(): User {
    if (!currentUser) {
        throw new Error('Settings user is not resolved');
    }
    return currentUser;
}

export function setEditingTeamId(id: number | null): void {
    editingTeamId = id;
}

/** Loads assignable users (Checkpoint) and teams (PM workspace). */
export async function initSettingsData(): Promise<void> {
    if (!app.window.member?.reference_num) {
        throw new Error('Authenticated member is missing reference_num');
    }

    await loadUsersFromCheckpoint();

    const workspace = await fetchSettingsWorkspace();
    if (workspace?.teams?.length) {
        replaceArrayInPlace(teams, workspace.teams);
    }

    persistenceEnabled = workspace !== null;
    await resolveCurrentUserFromAuth();
}

export function persistTeams(): void {
    if (!persistenceEnabled) {
        return;
    }

    if (workspaceSaveTimer) {
        clearTimeout(workspaceSaveTimer);
    }

    workspaceSaveTimer = setTimeout(() => {
        if (!isSettingsAdmin()) {
            return;
        }
        void saveSettingsTeams([...teams]);
    }, 500);
}
